import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconCircleInfoOutline18 as InfoIcon,
	IconMagnifierOutline18 as SearchIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Badge,
	Button,
	Input,
	SortableTableHeader,
	Tooltip,
	Virtualizer,
} from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { FilterChip } from "@/components/filters"
import type { RouterOutputs } from "@/lib/api"
import { FilterControlChips } from "@/components/filters"
import { ReportingChartFrame } from "@/components/reporting/charts"
import {
	reportingCardClassName,
	reportingCardLoadingClassName,
} from "@/components/reporting/charts/theme"
import { api } from "@/lib/api"

type Data = RouterOutputs["analytics"]["aiAutomationRates"]
type Row = Data["rows"][number]

type SortKey = "name" | "eligible" | "involvement" | "resolution" | "automation"
type SortType = `${SortKey}-asc` | `${SortKey}-desc`

const ELIGIBLE_DEFINITION =
	"Eligible threads: created in the window, with at least one inbound customer message. Threads a teammate opened, deleted and merged threads are left out. Spam is not stored on threads, so it still counts."

const NOT_BILLING =
	"Reporting number. Billing counts a narrower resolution, so this runs higher than the invoice."

const METRICS = [
	{
		key: "involvement",
		label: "Involvement rate",
		meaning: "How often the AI agent joins a customer conversation.",
		definition: `Threads the AI agent took part in, divided by all eligible threads. ${ELIGIBLE_DEFINITION}`,
	},
	{
		key: "resolution",
		label: "Resolution rate",
		meaning:
			"How often the AI agent closes a conversation it joined, with no teammate stepping in.",
		definition:
			"Threads the AI agent resolved, divided by the threads it took part in. Resolved means no teammate replied and the thread closed, or the agent marked its session resolved. Same rule as the AI resolution rate chart.",
	},
	{
		key: "automation",
		label: "Automation rate",
		meaning:
			"How much of all customer conversations the AI agent closes on its own.",
		definition: `Threads the AI agent resolved, divided by all eligible threads. Equals involvement times resolution. ${ELIGIBLE_DEFINITION}`,
	},
] as const

const percent = (value: number | null) =>
	value === null ? "n/a" : `${Math.round(value * 1000) / 10}%`

const rateOf = (row: Row, key: SortKey): number | null => {
	if (key === "involvement") return row.involvementRate
	if (key === "resolution") return row.resolutionRate
	if (key === "automation") return row.automationRate
	return row.eligible
}

function MetricDefinition({
	meaning,
	definition,
}: {
	meaning?: string
	definition: string
}) {
	return (
		<div className="flex w-64 flex-col gap-1.5 py-0.5">
			{meaning ? <p className="font-medium">{meaning}</p> : null}
			<p>{definition}</p>
			<p className="text-tertiary">{NOT_BILLING}</p>
		</div>
	)
}

// Same look as ReportingKpiCard plus an info icon; kept here so the shared card stays untouched.
function RateCard({
	label,
	description,
	value,
	hint,
	isLoading,
}: {
	label: string
	description: ReactNode
	value: string
	hint?: string
	isLoading: boolean
}) {
	if (isLoading) {
		return (
			<div
				className={cn(
					reportingCardLoadingClassName,
					"relative overflow-hidden px-4 py-3.5",
				)}
			>
				<div className="mb-2 h-3 w-16 rounded bg-gray-200 dark:bg-gray-700" />
				<div className="h-7 w-12 rounded bg-gray-200 dark:bg-gray-700" />
			</div>
		)
	}

	return (
		<div
			className={cn(
				reportingCardClassName,
				"relative overflow-hidden px-4 py-3.5",
			)}
		>
			<div className="flex items-center gap-1.5">
				<p className="text-xs text-tertiary">{label}</p>
				<Tooltip label={description}>
					<span className="inline-flex text-tertiary">
						<InfoIcon className="h-3.5 w-3.5 flex-none" />
					</span>
				</Tooltip>
			</div>
			<p className="mt-1 text-2xl font-semibold tracking-tight text-primary">
				{value}
			</p>
			{hint ? <p className="mt-0.5 text-[11px] text-tertiary">{hint}</p> : null}
		</div>
	)
}

export function AiAutomationRates() {
	// Undefined lets the server apply its default exclusion list.
	const [excludedOverride, setExcludedIds] = useState<string[]>()
	const [sortType, setSortType] = useState<SortType>("eligible-desc")
	const [search, setSearch] = useState("")

	const query = api.analytics.aiAutomationRates.useQuery(
		{ excludedWorkspaceIds: excludedOverride },
		{ staleTime: 60000, keepPreviousData: true },
	)
	const data = query.data
	const isRefetching = query.isFetching && !query.isLoading

	const rows = useMemo(() => {
		const term = search.trim().toLowerCase()
		const filtered = (data?.rows ?? []).filter(
			(row) => !term || row.name.toLowerCase().includes(term),
		)
		const direction = sortType.endsWith("-asc") ? 1 : -1
		const key = sortType.slice(0, sortType.lastIndexOf("-")) as SortKey
		return [...filtered].sort((a, b) => {
			if (key === "name") return direction * a.name.localeCompare(b.name)
			const [left, right] = [rateOf(a, key), rateOf(b, key)]
			// No resolution rate sinks to the bottom in both directions.
			if (left === null || right === null) {
				return left === right ? 0 : left === null ? 1 : -1
			}
			return direction * (left - right)
		})
	}, [data?.rows, search, sortType])

	const defaults = data?.defaultExcludedWorkspaces ?? []
	const excludedIds = excludedOverride ?? defaults.map((w) => w.id)
	const toggle = (id: string) =>
		setExcludedIds(
			excludedIds.includes(id)
				? excludedIds.filter((x) => x !== id)
				: [...excludedIds, id],
		)

	const chips: FilterChip[] =
		excludedIds.length > 0
			? [
					{
						key: "excluded",
						label: "Workspace",
						middleText: "is not",
						valueText: defaults
							.filter((w) => excludedIds.includes(w.id))
							.map((w) => w.name)
							.join(", "),
						onClear: () => setExcludedIds([]),
						popoverContent: (
							<Command>
								<CommandList>
									<CommandGroup>
										{defaults.map((workspace) => (
											<CommandItem
												key={workspace.id}
												value={workspace.name}
												onSelect={() => toggle(workspace.id)}
											>
												<div className="flex w-full items-center gap-2">
													<span className="flex-1 truncate">
														{workspace.name}
													</span>
													<span className="text-tertiary">
														{workspace.reason}
													</span>
													<Check
														className={cn("h-4 w-4 flex-none", {
															"opacity-0": !excludedIds.includes(workspace.id),
														})}
													/>
												</div>
											</CommandItem>
										))}
									</CommandGroup>
								</CommandList>
							</Command>
						),
					},
				]
			: []

	const medianHint = (value: number | null) =>
		data
			? `Median customer ${percent(value)} across ${data.medians.workspaceCount} workspaces with ${data.minThreads}+ threads`
			: undefined

	const totalsByKey = data
		? {
				involvement: data.totals.involvementRate,
				resolution: data.totals.resolutionRate,
				automation: data.totals.automationRate,
			}
		: null
	const mediansByKey = data
		? {
				involvement: data.medians.involvementRate,
				resolution: data.medians.resolutionRate,
				automation: data.medians.automationRate,
			}
		: null

	return (
		<div className="flex flex-col gap-4">
			<FilterControlChips
				containerClassName="flex cursor-default select-none flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 dark:border-gray-700/80 dark:bg-gray-850"
				chips={chips}
				tailContent={
					<>
						{query.isLoading ? (
							<div className="h-6 w-64 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
						) : null}
						<p className="text-xs text-tertiary">
							{defaults
								.map((w) => `${w.name}: ${w.reason.toLowerCase()}`)
								.join(". ")}
							{defaults.length > 0 ? ". Left out by default." : null}
						</p>
						{excludedIds.length < defaults.length ? (
							<Button variant="ghost" onClick={() => setExcludedIds(undefined)}>
								Reset to default
							</Button>
						) : null}
					</>
				}
			/>

			<div
				className={cn(
					"grid grid-cols-1 gap-4 sm:grid-cols-3",
					isRefetching && "opacity-60",
				)}
			>
				{METRICS.map((metric) => (
					<RateCard
						key={metric.key}
						label={metric.label}
						description={
							<MetricDefinition
								meaning={metric.meaning}
								definition={metric.definition}
							/>
						}
						isLoading={query.isLoading}
						value={totalsByKey ? percent(totalsByKey[metric.key]) : "n/a"}
						hint={
							mediansByKey ? medianHint(mediansByKey[metric.key]) : undefined
						}
					/>
				))}
			</div>

			<ReportingChartFrame
				className={cn(isRefetching && "opacity-60")}
				title="AI rates by customer"
				description={
					data
						? `Last ${data.windowDays} days, in workspaces with the AI agent on and a thread in the last 2 weeks. Workspaces under ${data.minThreads} eligible threads stay in the table and the totals, but not in the median.`
						: undefined
				}
				isLoading={query.isLoading}
				isEmpty={!query.isLoading && rows.length === 0}
				emptyMessage={
					search ? "No workspace matches this search" : "No eligible threads"
				}
				legend={
					<Input
						name="ai-automation-workspace-search"
						variant="borderless"
						textSize="sm"
						icon={SearchIcon}
						placeholder="Find workspace..."
						value={search}
						className="w-44 px-0 placeholder:text-tertiary"
						inputClassName="h-7"
						onChange={(event) => setSearch(event.target.value)}
					/>
				}
			>
				<div className="overflow-x-auto">
					<div className="min-w-[560px] text-sm">
						<div className="flex items-center gap-3 border-b border-gray-200 pb-2 text-xs text-tertiary dark:border-gray-700/80">
							<div className="min-w-32 flex-1">
								<SortableTableHeader
									label="Workspace"
									currentSortType={sortType}
									sortTypeAsc="name-asc"
									sortTypeDesc="name-desc"
									onClick={setSortType}
								/>
							</div>
							<div className="w-20 flex-none">
								<Tooltip label={ELIGIBLE_DEFINITION}>
									<SortableTableHeader
										label="Threads"
										currentSortType={sortType}
										sortTypeAsc="eligible-asc"
										sortTypeDesc="eligible-desc"
										onClick={setSortType}
										arrowPosition="left"
										className="ml-auto"
									/>
								</Tooltip>
							</div>
							{METRICS.map((metric) => (
								<div key={metric.key} className="w-24 flex-none">
									<Tooltip
										label={
											<MetricDefinition
												meaning={metric.meaning}
												definition={metric.definition}
											/>
										}
									>
										<SortableTableHeader
											label={metric.label.replace(" rate", "")}
											currentSortType={sortType}
											sortTypeAsc={`${metric.key}-asc`}
											sortTypeDesc={`${metric.key}-desc`}
											onClick={setSortType}
											arrowPosition="left"
											className="ml-auto"
										/>
									</Tooltip>
								</div>
							))}
						</div>

						<Virtualizer
							className="max-h-[480px] pb-2"
							estimateSize={36}
							itemsData={rows}
							itemContent={(row) => (
								<div className="flex items-center gap-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-850/50">
									<div className="flex min-w-32 flex-1 items-center gap-2">
										<span className="truncate">{row.name}</span>
										{row.inMedian ? null : (
											<Tooltip
												label={`Under ${data?.minThreads ?? 0} eligible threads, so it stays out of the median.`}
											>
												<Badge variant="default" size="sm">
													Low volume
												</Badge>
											</Tooltip>
										)}
									</div>
									<Tooltip
										label={`${row.involved.toLocaleString("en-US")} involved, ${row.resolved.toLocaleString("en-US")} resolved`}
									>
										<div className="w-20 flex-none text-right tabular-nums text-secondary">
											{row.eligible.toLocaleString("en-US")}
										</div>
									</Tooltip>
									<div className="w-24 flex-none text-right tabular-nums">
										{percent(row.involvementRate)}
									</div>
									<div className="w-24 flex-none text-right tabular-nums">
										{percent(row.resolutionRate)}
									</div>
									<div className="w-24 flex-none text-right tabular-nums">
										{percent(row.automationRate)}
									</div>
								</div>
							)}
						/>
					</div>
				</div>
			</ReportingChartFrame>
		</div>
	)
}
